/**
 * Core logica voor het CASL-permissiesysteem (Signal-compatible).
 * Definieert subjects, acties en de logica om abilities te bouwen op basis van rollen.
 */

import { effect, Signal } from '@angular/core';
import { createMongoAbility } from '@casl/ability';
import { User } from 'oidc-client-ts';

import { ROLE_PERMISSIONS } from './permissions';
import { AbilityContext, AppAbility } from './types';

/**
 * Aggregeert regels van alle rollen die van toepassing zijn op de gebruiker.
 */
export const defineRulesFor = (user: User): AppAbility['rules'] => {
  const rules: AppAbility['rules'] = [];
  const context: AbilityContext = {
    can: (action, subject) => {
      rules.push({ action, subject });
    },
    cannot: (action, subject) => {
      rules.push({ action, subject, inverted: true });
    },
  };

  const userRoles = (user?.profile?.['group_membership'] ?? []) as string[];

  ROLE_PERMISSIONS.filter(roleDef => roleDef.name === 'default' || userRoles.includes(roleDef.name))
    .sort((a, b) => a.priority - b.priority)
    .forEach(roleDef => {
      roleDef.define(context, user);
    });

  return rules;
};

/**
 * Bouwt het ability object dat gebruikt wordt in de Angular frontend.
 * Deze instance kan direct in de AbilityServiceSignal geïnjecteerd worden.
 */
export const buildSignalAbility = (userSignal: Signal<User | null>): AppAbility => {
  const ability = createMongoAbility<AppAbility>();
  effect(() => {
    const user = userSignal();
    if (user) {
      const rules = defineRulesFor(user);
      ability.update(rules);
    } else {
      // Wis alle permissies als er geen gebruiker is (bijv. na logout)
      ability.update([]);
    }
  });

  return ability;
};
